import React, { useState, ChangeEvent, FormEvent } from "react";
import { HouseFormData } from "./types";
import { ApiService, ValidationUtils, FormatUtils } from "../utils/api";

const initialFormData: HouseFormData = {
  nombre: "",
  email: "",
  telefono: "",
  direccion: "",
  tipo: "piso",
  metros: undefined,
  habitaciones: undefined,
  banos: undefined,
  precio: "",
  estado: "bueno",
  descripcion: "",
  razon: "otro",
  // fotos: []
};

const HouseForm: React.FC = () => {
  const [formData, setFormData] = useState<HouseFormData>(initialFormData);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [errors, setErrors] = useState<string[]>([]);

  const handleChange = (
    e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "metros" ? (value ? parseInt(value) : undefined) : value,
    }));
  };

  // const handleFileChange = (e: ChangeEvent<HTMLInputElement>) => {
  //   const newFiles: File[] = e.target.files ? Array.from(e.target.files) : [];

  //   setFormData((prev) => {
  //     const combinedFiles = [...prev.fotos, ...newFiles];

  //     // Filtrar duplicados por nombre
  //     const uniqueFiles = combinedFiles.filter(
  //       (file, index, self) =>
  //         index === self.findIndex((f) => f.name === file.name)
  //     );

  //     // Validaciones
  //     const fileErrors: string[] = [];
  //     if (uniqueFiles.length > 10) {
  //       fileErrors.push("Máximo 10 fotos permitidas");
  //     }

  //     uniqueFiles.forEach((file) => {
  //       if (file.size > 5 * 1024 * 1024) {
  //         fileErrors.push(
  //           `La foto "${file.name}" es demasiado grande (máximo 5MB)`
  //         );
  //       }
  //       if (!file.type.match(/^image\/(jpeg|jpg|png|gif)$/)) {
  //         fileErrors.push(
  //           `La foto "${file.name}" no es un formato válido (JPG, PNG, GIF)`
  //         );
  //       }
  //     });

  //     setErrors(fileErrors);

  //     return fileErrors.length === 0 ? { ...prev, fotos: uniqueFiles } : prev;
  //   });

  //   // Resetear input
  //   if (e.target) e.target.value = "";
  // };

  const resetForm = () => {
    setFormData(initialFormData);
    setErrors([]);
    setMessage(null);
    // const fileInput = document.getElementById("fotos") as HTMLInputElement;
    // if (fileInput) {
    //   fileInput.value = "";
    // }
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const validationErrors = ValidationUtils.validateFormData(formData);
    if (validationErrors.length > 0) {
      setErrors(validationErrors);
      setMessage({
        type: "error",
        text: "Por favor, corrige los errores antes de enviar",
      });
      return;
    }

    setIsSubmitting(true);
    setErrors([]);
    setMessage(null);

    try {
      const response = await ApiService.submitHouseForm(formData);

      if (response.success) {
        setMessage({
          type: "success",
          text:
            response.message ||
            "Formulario enviado correctamente. Te contactaremos en 24-48 horas.",
        });
        resetForm();
        const formElement = document.getElementById("formulario");
        if (formElement) {
          formElement.scrollIntoView({ behavior: "smooth" });
        }
      } else {
        setMessage({
          type: "error",
          text:
            response.message ||
            "Error al enviar el formulario. Inténtalo de nuevo.",
        });
      }
    } catch (error) {
      setMessage({
        type: "error",
        text: "Error de comunicación. Verifica tu conexión e inténtalo de nuevo.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="formulario" className="form-section">
      <div className="container">
        <div className="form-container">
          <h2>Sube los Datos de tu Casa</h2>

          {message && (
            <div className={`message ${message.type}`}>{message.text}</div>
          )}

          {errors.length > 0 && (
            <div className="message error">
              <ul style={{ margin: 0, paddingLeft: "20px" }}>
                {errors.map((error, index) => (
                  <li key={index}>{error}</li>
                ))}
              </ul>
            </div>
          )}

          <form onSubmit={handleSubmit} encType="multipart/form-data">
            <h3>Información Personal</h3>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="nombre">
                  Nombre Completo <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="nombre"
                  name="nombre"
                  value={formData.nombre}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="email">
                  Email <span className="required">*</span>
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="telefono">
                Teléfono <span className="required">*</span>
              </label>
              <input
                type="tel"
                id="telefono"
                name="telefono"
                value={formData.telefono}
                onChange={handleChange}
                placeholder="Ej: +34 600 123 456"
                required
                disabled={isSubmitting}
              />
            </div>

            <h3>Información de la Propiedad</h3>

            <div className="form-group">
              <label htmlFor="direccion">
                Dirección Completa <span className="required">*</span>
              </label>
              <input
                type="text"
                id="direccion"
                name="direccion"
                value={formData.direccion}
                onChange={handleChange}
                placeholder="Calle, número, piso, ciudad, código postal"
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="tipo">Tipo de Propiedad</label>
                <select
                  id="tipo"
                  name="tipo"
                  value={formData.tipo}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="piso">Piso</option>
                  <option value="casa">Casa</option>
                  <option value="chalet">Chalet</option>
                  <option value="duplex">Dúplex</option>
                  <option value="estudio">Estudio</option>
                  <option value="local">Local</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="metros">Metros Cuadrados</label>
                <input
                  type="number"
                  id="metros"
                  name="metros"
                  value={formData.metros || ""}
                  onChange={handleChange}
                  placeholder="Ej: 90"
                  min="1"
                  disabled={isSubmitting}
                />
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="habitaciones">Habitaciones</label>
                <select
                  id="habitaciones"
                  name="habitaciones"
                  value={formData.habitaciones || ""}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">Seleccionar...</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4">4</option>
                  <option value="5+">5 o más</option>
                </select>
              </div>
              <div className="form-group">
                <label htmlFor="banos">Baños</label>
                <select
                  id="banos"
                  name="banos"
                  value={formData.banos || ""}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="">Seleccionar...</option>
                  <option value="1">1</option>
                  <option value="2">2</option>
                  <option value="3">3</option>
                  <option value="4+">4 o más</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="precio">
                  Precio Solicitado <span className="required">*</span>
                </label>
                <input
                  type="text"
                  id="precio"
                  name="precio"
                  value={formData.precio}
                  onChange={handleChange}
                  placeholder="Ej: 150.000€"
                  required
                  disabled={isSubmitting}
                />
              </div>
              <div className="form-group">
                <label htmlFor="estado">Estado de la Propiedad</label>
                <select
                  id="estado"
                  name="estado"
                  value={formData.estado || ""}
                  onChange={handleChange}
                  disabled={isSubmitting}
                >
                  <option value="excelente">Excelente</option>
                  <option value="bueno">Bueno</option>
                  <option value="aceptable">Aceptable</option>
                  <option value="reforma">Necesita reforma</option>
                </select>
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="descripcion">
                Descripción Detallada <span className="required">*</span>
              </label>
              <textarea
                id="descripcion"
                name="descripcion"
                value={formData.descripcion}
                onChange={handleChange}
                placeholder="Describe tu casa: orientación, luminosidad, equipamiento, zona, parking, trastero, etc."
                required
                disabled={isSubmitting}
              />
            </div>

            <div className="form-group">
              <label htmlFor="razon">Razón de Venta</label>
              <select
                id="razon"
                name="razon"
                value={formData.razon || ""}
                onChange={handleChange}
                disabled={isSubmitting}
              >
                <option value="cambio_residencia">Cambio de residencia</option>
                <option value="inversion">Inversión</option>
                <option value="herencia">Herencia</option>
                <option value="problemas_economicos">
                  Problemas económicos
                </option>
                <option value="otro">Otro</option>
              </select>
            </div>

            {/* <div className="form-group">
              <label htmlFor="fotos">
                Fotos de la Propiedad <span className="required">*</span>
              </label>
              <div className="file-upload">
                <p>📷 Sube hasta 10 fotos de tu casa</p>
                <p>Incluye: salón, cocina, habitaciones, baños, exterior</p>
                <p>Formatos: JPG, PNG, GIF (máximo 5MB por foto)</p>
                <input
                  type="file"
                  id="fotos"
                  name="fotos[]"
                  multiple
                  // accept="image/jpeg,image/jpg,image/png,image/gif"
                  accept="image/*"
                  onChange={handleFileChange}
                  required
                  disabled={isSubmitting}
                />
                {formData.fotos.length > 0 && (
                  <div className="preview-container">
                    <p>
                      {formData.fotos.length} foto
                      {formData.fotos.length !== 1 ? "s" : ""} seleccionada
                      {formData.fotos.length !== 1 ? "s" : ""} (
                      {FormatUtils.formatFileSize(
                        formData.fotos.reduce(
                          (total, file) => total + file.size,
                          0
                        )
                      )}
                      )
                    </p>

                    <div
                      className="preview-grid"
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: "10px",
                        marginTop: "10px",
                      }}
                    >
                      {formData.fotos.map((file, index) => (
                        <div
                          key={index}
                          style={{
                            position: "relative",
                            width: "100px",
                            height: "100px",
                            border: "1px solid #ccc",
                            borderRadius: "8px",
                            overflow: "hidden",
                          }}
                        >
                          <img
                            src={URL.createObjectURL(file)}
                            alt={`preview-${index}`}
                            style={{
                              width: "100%",
                              height: "100%",
                              objectFit: "cover",
                            }}
                          />
                          <button
                            type="button"
                            onClick={() => {
                              // Permite eliminar la foto seleccionada antes de enviar
                              setFormData((prev) => ({
                                ...prev,
                                fotos: prev.fotos.filter((_, i) => i !== index),
                              }));
                            }}
                            style={{
                              position: "absolute",
                              top: 2,
                              right: 2,
                              background: "rgba(0,0,0,0.6)",
                              color: "white",
                              border: "none",
                              borderRadius: "50%",
                              cursor: "pointer",
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              fontSize: "12px",
                            }}
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </div> */}

            <div className="form-group">
              <label>
                <input type="checkbox" required disabled={isSubmitting} />
                Acepto el tratamiento de mis datos personales y autorizo la
                publicación de las fotos para el proceso de venta{" "}
                <span className="required">*</span>
              </label>
            </div>

            <button
              type="submit"
              className="submit-btn"
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <>
                  <span className="loading"></span>
                  Enviando...
                </>
              ) : (
                "Enviar Información de mi Casa"
              )}
            </button>
          </form>
        </div>
      </div>
    </section>
  );
};

export default HouseForm;
